<template>
  <div id="navigation-top">
    <nav class="navbar navbar-dark bg-primary navbar-expand justify-content-start"
      :class="[became && 'became']"
      id="top-navigation">
      <div class="hamburger" @click="toggle" ref="hamburger">
        <i class="fas fa-bars fa-fw"/>
      </div>

      <div class="collapse navbar-collapse" v-if="companies.length > 1">
        <label class="navbar-text mr-3 d-none d-xl-inline-block my-auto">Active&nbsp;Company:</label>
        <multiselect
          class="single_required multiselect-sm"
          :options="companies"
          label="name"
          track-by="id"
          v-model="selectedCompany"
          @input="changeCompany"
          :show-labels="false"
          :allow-empty="false"
          :loading="changingCompany"
          :disabled="changingCompany"
        >
        </multiselect>
      </div>

      <div class="collapse navbar-collapse justify-content-end">
        <NavigationTopPublishStatus v-show="loggedInUser && hasAccessConfigurationOptions"/>
        <div class="navbar-nav">
          <b-dd id="userLink" variant="link" size="sm">
            <template slot="button-content">
              <span class="d-none d-xl-inline-block">Welcome,</span>
              {{ loggedInUser ? loggedInUser.userDisplayName : '' }}
            </template>
            <b-dropdown-item @click="changePassword">Change Password</b-dropdown-item>
            <b-dropdown-item @click="logout" v-if="!inIframe">Logout</b-dropdown-item>
          </b-dd>
        </div>
      </div>
    </nav>

    <NavigationTopUnpublishedLogoutStatus
      v-if="showUnpublishedDialog"
      :resolve="unpublishedResolve"
    />
  </div>
</template>

<script>
import navigationService from './navigation-service'
import velocity from 'velocity-animate'
import userInfoMixin from '@/common/mixins/user-info-mixin'
import exceptionMixin from '@/common/mixins/exception-mixin'
import { EventBus, events } from '@/common/EventBus'
import alertsMixin from '@/common/mixins/alerts-mixin'
import { mapState, mapGetters } from 'vuex'
import NavigationTopPublishStatus from '@/navigation/NavigationTopPublishStatus'
import appFeatures from '@/common/app-features'
import _ from 'lodash'

const NavigationTopUnpublishedLogoutStatus = () => import('@/navigation/NavigationTopUnpublishedLogoutStatus')

export default {
  name: 'navigation-top',
  components: { NavigationTopUnpublishedLogoutStatus, NavigationTopPublishStatus },
  mixins: [userInfoMixin, exceptionMixin, alertsMixin],
  data () {
    return {
      routes: [],
      companies: [],
      selectedCompany: null,
      showUnpublishedDialog: false,
      unpublishedResolve: null
    }
  },
  computed: {
    ...mapState('login', ['changingCompany']),
    ...mapGetters('menuPublish', ['hasUnpublished']),
    hasAccessConfigurationOptions () {
      return this.hasFeature(appFeatures.accessConfigurationOptions)
    },
    inIframe () {
      try {
        return window.self !== window.top
      } catch (e) {
        return true
      }
    },
    became () {
      return this.loggedInUser && this.loggedInUser.originalUserId
    }
  },
  created () {
    this.unwatchUser = this.$store.watch(state => state.login.user, () => {
      this.routes = navigationService.getNavigationLeft(this.enabledFeatures)
      this.getAccessibleCompanies()

      if (this.loggedInUser) {
        this.checkSidebar()
      }
    })
  },
  mounted () {
    this.routes = navigationService.getNavigationLeft(this.enabledFeatures)
    EventBus.$on(events.companyAdded, this.getAccessibleCompanies)
    EventBus.$on(events.companyRemoved, this.companyRemoved)
    this.checkSidebar()
  },
  beforeDestroy () {
    this.unwatchUser()
    EventBus.$off(events.companyAdded)
    EventBus.$off(events.companyRemoved)
    velocity.Utilities.removeData(this.$refs.hamburger)
  },
  methods: {
    async logout () {
      if (this.hasUnpublished && this.hasAccessConfigurationOptions) {
        const response = await new Promise(resolve => {
          this.unpublishedResolve = resolve
          this.showUnpublishedDialog = true
        })

        this.showUnpublishedDialog = false
        if (!response) {
          return
        }
      }
      try {
        await this.$store.dispatch('login/logout')
        this.$router.push({ name: 'login' }).catch(() => {})
      } catch (error) {
        this.handleError(error, 'Unable to Log Out, please try again later.')
      }
    },
    changePassword () {
      this.$router.push({ name: 'changePassword' }).catch(() => {})
    },
    toggle () {
      this.$store.commit('SET_SIDEBAR_COLLAPSED', !this.$store.state.main.sidebarCollapsed)
    },
    async getAccessibleCompanies () {
      if (!this.loggedInUser) {
        return Promise.resolve()
      }

      try {
        const companies = await navigationService.getAccessibleCompanies()
        this.companies = companies.sort((a, b) => a.name.localeCompare(b.name))

        const fromQuery = companies.find(it => it.id === this.$route.query.companyId)
        if (fromQuery) {
          this.selectedCompany = fromQuery
        } else {
          this.selectedCompany = this.companies.find(next => next.id === this.userCompanyId)
        }

        if (this.selectedCompany && this.selectedCompany.id !== _.get(this.$store.state.login.user, 'userCompanyId')) {
          this.changeCompany(this.selectedCompany)
        }
      } catch (error) {
        this.handleError(error, 'Unable to retrieve accessible companies')
      }
    },
    changeCompany (selected) {
      if (this.$store.state.main.dirty.length > 0) {
        let message = `Are you sure you want to change the active Company? Any unsaved changes will be lost.`
        this.openConfirm({
          message,
          title: 'Confirm Company Change',
          acceptText: 'Change',
          acceptClass: 'btn-warning'
        })
          .then(result => {
            if (result) {
              this.$router.push({ name: 'changeCompany', query: { companyId: selected.id } }).catch(() => {})
            }
          })
      } else {
        this.$router.push({ name: 'changeCompany', query: { companyId: selected.id } }).catch(() => {})
      }
      // Revert in case user cancels confirmation, whether here or on the route change.
      this.selectedCompany = this.companies.find(next => next.id === this.userCompanyId)
    },
    companyRemoved (company) {
      let selectGranbury = company.id === this.selectedCompany.id

      this.getAccessibleCompanies()
        .then(() => {
          if (selectGranbury) {
            let granburyCompany = this.companies.find(next => this.isGranburyCompany(next))
            if (granburyCompany) {
              this.$router.push({ name: 'changeCompany', query: { companyId: granburyCompany.id } }).catch(() => {})
            }
          }
        })
    },
    checkSidebar () {
      let width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth

      if (width < 1200 && !this.sidebarCollapsed) {
        setTimeout(() => {
          this.$store.commit('SET_SIDEBAR_COLLAPSED', true)

          velocity(this.$refs.hamburger, { backgroundColor: '#FFF', backgroundColorAlpha: '0.2' }, 500)
          velocity(this.$refs.hamburger, { backgroundColor: '#FFF', backgroundColorAlpha: '0' }, 300)
          velocity(this.$refs.hamburger, { backgroundColor: '#FFF', backgroundColorAlpha: '0.2' }, 500)
          velocity(this.$refs.hamburger, { backgroundColor: '#FFF', backgroundColorAlpha: '0' }, 300)
        }, 1500)
      }
    }
  }
}
</script>

<style lang="scss">
  #navigation-top {
    .nav-link, .navbar-text {
      color: white !important;
    }

    #userLink button.btn-link {
      color: white !important;

      &:hover {
        text-decoration: none;
        cursor: pointer;
      }
    }

    .navbar-text {
      font-size: 0.8rem;
    }

    .hamburger {
      padding: 5px;
      border: none;
      font-size: 1em;
      color: white;
      margin-right: 1em;
      cursor: pointer;
      border-radius: 50%;

      &:hover {
        background-color: rgba(255, 255, 255, 0.2) !important;
      }
    }

    .became {
      background-color: #a00 !important;
    }}
</style>
